import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Box, Typography, Pagination } from '@mui/material';
import { DriverCard, PageHeader, RegisterDriverModal, DriverSettingsModal } from './components';
import { useToast } from '../../context/toastContext';
import { Driver } from '../../models/driver';
import { apiGet } from '../../services/apiClient';
import { useTranslation } from 'react-i18next';
import { CardSkeletonLoader } from '../../components/loaders';
import { AuthContext } from '../../context/authContext';
import { UserPlatformSubRole } from '../../models/user';

const DriversPage: React.FC = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);

  // Check if user is a truck driver (read-only access)
  const isDriver = authContext?.user?.subRole === UserPlatformSubRole.TRUCK_DRIVER;

  const [viewMode, setViewMode] = useState<'cards' | 'map'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | string[]>('all');
  const [countryFilter, setCountryFilter] = useState<string | string[]>('all');
  const [certificationFilter, setCertificationFilter] = useState<string | string[]>('all');
  const [selectedStatusTab, setSelectedStatusTab] = useState<string>('all');
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);

  const toast = useToast();

  useEffect(() => {
    fetchDrivers();
  }, []);
  const fetchDrivers = async () => {
    try {
      setLoading(true);
      const response = await apiGet(`${process.env.REACT_APP_API_URL}/api/v1/drivers`);
      const data = await response.json();

      // Handle different possible response structures
      let driversArray = [];
      if (Array.isArray(data)) {
        driversArray = data;
      } else if (data && Array.isArray(data.result)) {
        driversArray = data.result;
      } else if (data && Array.isArray(data.drivers)) {
        driversArray = data.drivers;
      } else if (data && Array.isArray(data.data)) {
        driversArray = data.data;
      } else if (data && typeof data === 'object') {
        // If it's a single driver object, wrap it in an array
        driversArray = [data];
      }

      // Map API response to our Driver model format
      const mappedDrivers = driversArray.map((driver: any) => {
        // Parse mainLocation with better handling
        const mainLocation = driver.mainLocation || driver.main_location || {};
        const latitude = typeof mainLocation.latitude === 'number' ? mainLocation.latitude :
          (typeof mainLocation.lat === 'number' ? mainLocation.lat : 0);
        const longitude = typeof mainLocation.longitude === 'number' ? mainLocation.longitude :
          (typeof mainLocation.lng === 'number' ? mainLocation.lng :
            (typeof mainLocation.lon === 'number' ? mainLocation.lon : 0));

        return {
          id: driver.id,
          tenantId: driver.tenantId || driver.tenant_id,
          userId: driver.userId || driver.user_id,
          firstName: driver.firstName || driver.first_name || '',
          lastName: driver.lastName || driver.last_name || '',
          email: driver.email,
          mainLocation: {
            latitude: latitude,
            longitude: longitude
          },
          knownLanguages: driver.knownLanguages || driver.known_languages || [],
          safetyCertificates: driver.safetyCertificates || driver.safety_certificates || [],
          baseCountry: driver.baseCountry || driver.base_country || '',
          isActive: driver.isActive !== undefined ? driver.isActive : (driver.is_active !== undefined ? driver.is_active : true),
          createdAt: driver.createdAt || driver.created_at || driver.updatedOn,
          updatedAt: driver.updatedAt || driver.updated_at || driver.updatedOn
        };
      });
      setDrivers(mappedDrivers);
    } catch (_) {
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDriverClick = (driver: Driver) => {
    setSelectedDriver(driver);
    setShowSettingsModal(true);
  };

  const filteredDrivers = useMemo(() => {
    if (!Array.isArray(drivers)) {
      return [];
    }
    return drivers.filter(driver => {
      const fullName = `${driver.firstName} ${driver.lastName}`.toLowerCase();
      const matchesSearch = searchTerm === '' ||
        fullName.includes(searchTerm.toLowerCase()) ||
        driver.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.lastName.toLowerCase().includes(searchTerm.toLowerCase());

      // Handle multi-select status filter
      const statusArray = Array.isArray(statusFilter) ? statusFilter : (statusFilter === 'all' ? [] : [statusFilter]);
      const matchesStatus = statusArray.length === 0 ||
        statusArray.some(status => (status === 'active' && driver.isActive) ||
          (status === 'inactive' && !driver.isActive)
        );

      // Handle multi-select country filter
      const countryArray = Array.isArray(countryFilter) ? countryFilter : (countryFilter === 'all' ? [] : [countryFilter]);
      const matchesCountry = countryArray.length === 0 || countryArray.includes(driver.baseCountry || '');

      // Handle multi-select certification filter
      const certificationArray = Array.isArray(certificationFilter) ? certificationFilter : (certificationFilter === 'all' ? [] : [certificationFilter]);
      const matchesCertification = certificationArray.length === 0 ||
        (driver.safetyCertificates &&
          certificationArray.some(cert => driver.safetyCertificates?.includes(cert)));

      return matchesSearch && matchesStatus && matchesCountry && matchesCertification;
    });
  }, [drivers, searchTerm, statusFilter, countryFilter, certificationFilter]);

  const totalPages = Math.ceil(filteredDrivers.length / itemsPerPage);
  const paginatedDrivers = filteredDrivers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const countries = useMemo(() => {
    const uniqueCountries = [...new Set(drivers.map(d => d.baseCountry).filter(Boolean))] as string[];
    return uniqueCountries.sort();
  }, [drivers]);

  const certifications = useMemo(() => {
    const allCertifications = drivers.flatMap(d => d.safetyCertificates || []);
    const uniqueCertifications = [...new Set(allCertifications)];
    return uniqueCertifications.sort();
  }, [drivers]);

  // Calculate status tab counts
  const statusTabs = useMemo(() => {
    const all = drivers.length;
    const active = drivers.filter(d => d.isActive).length;
    const inactive = drivers.filter(d => !d.isActive).length;

    return [
      { id: 'all', label: 'All', count: all },
      { id: 'active', label: 'Active', count: active },
      { id: 'inactive', label: 'Inactive', count: inactive }
    ];
  }, [drivers]);

  // Handle status tab change
  const handleStatusTabChange = (status: string) => {
    setSelectedStatusTab(status);
    if (status === 'all') {
      setStatusFilter('all');
    } else {
      setStatusFilter([status]);
    }
    setCurrentPage(1);
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
      <PageHeader
        totalDrivers={drivers.length}
        statusTabs={statusTabs}
        selectedStatus={selectedStatusTab}
        onStatusChange={handleStatusTabChange}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onRegisterDriver={!isDriver ? () => setShowRegisterModal(true) : undefined}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        certificationFilter={certificationFilter}
        setCertificationFilter={setCertificationFilter}
        countries={countries}
        certifications={certifications}
      />

      {viewMode === 'cards' && (
        <Box sx={styles.modernDriversGrid}>
          <CardSkeletonLoader
            loading={loading}
            count={itemsPerPage}
            showImage={false}
            showTitle={true}
            showDescription={true}
            showActions={false}
            minHeight={280}
            sx={{
              ...styles.driversContainer,
              display: 'grid',
            }}
          >
            <Box sx={styles.driversContainer}>
              {paginatedDrivers.map((driver) => (
                <DriverCard
                  key={driver.id}
                  driver={driver}
                  onClick={handleDriverClick}
                />
              ))}
            </Box>
          </CardSkeletonLoader>

          {totalPages > 1 && (
            <Box sx={styles.paginationContainer}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'map' && (
        <Box sx={styles.mapPlaceholder}>
          <Typography variant="h6" color="text.secondary">
            {t('Map view coming soon...')}
          </Typography>
        </Box>
      )}

      {filteredDrivers.length === 0 && !loading && (
        <Box sx={styles.noResults}>
          <Typography variant="h6" gutterBottom>
            {drivers.length === 0 ? t('No drivers registered yet') : t('No drivers found')}
          </Typography>
          <Typography variant="body2">
            {drivers.length === 0
              ? t('Click "Register Driver" to add your first driver')
              : t('Try adjusting your search or filters')
            }
          </Typography>
        </Box>
      )}

      <DriverSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        driver={selectedDriver}
        readOnly={isDriver}
        onSuccess={(message) => {
          fetchDrivers();
          toast.success(message);
        }}
      />

      <RegisterDriverModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={() => {
          fetchDrivers();
          toast.success(t('Driver Registered Successfully!'));
        }}
      />
    </Box>
  );
};

const styles = {
  modernDriversGrid: {
    flex: 1
  },
  driversContainer: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',                    // Mobile: 1 column
      sm: 'repeat(2, 1fr)',         // Tablet: 2 columns
      md: 'repeat(3, 1fr)',         // Small Desktop: 3 columns
      lg: 'repeat(4, 1fr)',         // Desktop: 4 columns
      xl: 'repeat(6, 1fr)',        // Large Desktop (1536px+): 6 columns
      xxl: 'repeat(8, 1fr)',       // Ultra-wide (2400px+): 8 columns
    },
    gap: {
      xs: 1.5,      // Mobile: tighter spacing
      sm: 2,        // Tablet: standard spacing
      md: 2,        // Desktop: standard spacing
      lg: 2.5,      // Large: slightly more spacing
      xl: 2.5,      // Very large: balanced spacing for 6 columns
      xxl: 2.5      // Ultra-wide: balanced spacing for 8 columns
    },
    mb: { xs: 2, sm: 3 },
    // Prevent cards from becoming too wide on ultra-wide screens
    maxWidth: { xl: '100%' },
    width: '100%'
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    pt: 2,
    borderTop: '1px solid rgba(0,0,0,0.05)'
  },
  mapPlaceholder: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.white',
    borderRadius: 2
  },
  noResults: {
    textAlign: 'center',
    py: 8,
    color: 'text.secondary'
  }
};

export default DriversPage;
